import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, useTheme } from '@primer/react';
import { ThreeBarsIcon, XIcon } from '@primer/octicons-react';
import pegasusLogo from '../assets/brand/pegasus-logo.png';

const navItems = [
  { label: 'Services', to: '/services' },
  { label: 'How We Work', to: '/how-we-work' },
  { label: 'Insights', to: '/insights' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

function Header() {
  const location = useLocation();
  const { resolvedColorMode } = useTheme();
  const isDark = resolvedColorMode === 'night';
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const surfaceSx = {
    border: '1px solid',
    borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(27, 31, 36, 0.22)',
    backgroundColor: isDark ? 'rgba(13, 17, 23, 0.75)' : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow: 'shadow.large',
  };

  return (
    <Box
      as="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        paddingTop: 3,
        paddingBottom: 3,
        paddingX: ['24px', '48px', '100px'],
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        <Box
          as={RouterLink}
          to="/"
          aria-label="Pegasus UX home"
          sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
        >
          <Box
            as="img"
            src={pegasusLogo}
            alt="Pegasus UX"
            sx={{ height: '44px', width: '44px', filter: isDark ? 'none' : 'invert(1)' }}
          />
        </Box>

        <Box
          as="button"
          type="button"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          sx={{
            ...surfaceSx,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '44px',
            height: '44px',
            padding: 0,
            borderRadius: '50%',
            color: 'fg.default',
            cursor: 'pointer',
          }}
        >
          {isMenuOpen ? <XIcon size={20} /> : <ThreeBarsIcon size={20} />}
        </Box>

        {isMenuOpen && (
          <Box
            as="nav"
            sx={{
              ...surfaceSx,
              position: 'absolute',
              top: 'calc(100% + 10px)',
              right: 0,
              zIndex: 100,
              display: 'flex',
              flexDirection: 'column',
              minWidth: '220px',
              padding: 4,
              borderRadius: '24px',
              gap: 4,
            }}
          >
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
              return (
                <Box
                  as={RouterLink}
                  key={item.to}
                  to={item.to}
                  sx={{
                    textDecoration: 'none',
                    fontSize: 2,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'fg.default' : 'fg.muted',
                    whiteSpace: 'nowrap',
                    ':hover': { color: 'fg.default' },
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Header;
